import React from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';

/**
 * LibraryCardSkeleton
 *
 * Matches the outer chrome of LibraryCard exactly:
 * - Outer borderRadius: theme.radius.md (12px) — same as GradientSurface default
 * - Image area: 16/10 aspect ratio
 * - Content padding: p: 2.5 (20px)
 *
 * No props — all skeletons are identical.
 */
function LibraryCardSkeleton() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // Match GradientSurface's surface variant border-radius (theme.radius?.md ?? 12)
  const outerRadius = theme.radius?.md ?? 12;

  // Soft gradient bg so the wave skeleton animates over a realistic image placeholder
  const imageBg = isLight
    ? 'linear-gradient(135deg, rgba(15,23,42,0.04), rgba(15,23,42,0.02))'
    : 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))';

  return (
    <Box
      aria-hidden="true"
      sx={{
        borderRadius: `${outerRadius}px`,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.shadowsExt?.sm ?? '0 2px 6px rgba(15,23,42,0.06)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image area — 16/10 aspect ratio, matching LibraryCard */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16/10',
          overflow: 'hidden',
          background: imageBg,
        }}
      >
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '0px',
            transform: 'none',
            // Override MUI Skeleton's default transform so it fills the aspect-ratio box
            '&::after': { animationDuration: '1.6s' },
          }}
        />
      </Box>

      {/* Content — p: 2.5 matches LibraryCard */}
      <Box sx={{ flexGrow: 1, p: 2.5 }}>
        {/* Title — two lines: full width + 60% */}
        <Skeleton variant="text" animation="wave" width="100%" sx={{ mb: 0.5, borderRadius: '4px' }} />
        <Skeleton variant="text" animation="wave" width="60%" sx={{ mb: 2, borderRadius: '4px' }} />

        {/* Location line */}
        <Skeleton variant="text" animation="wave" width="40%" sx={{ mb: 2, borderRadius: '4px' }} />

        {/* Chips row — seats + price */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <Skeleton variant="rounded" animation="wave" width={56} height={20} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rounded" animation="wave" width={56} height={20} sx={{ borderRadius: '6px' }} />
        </Box>

        {/* CTA / price — aligned right */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" animation="wave" width={80} height={28} sx={{ borderRadius: '6px' }} />
        </Box>
      </Box>
    </Box>
  );
}

export default LibraryCardSkeleton;
